// uiux-live mount gate — the explicit production counterpart of
// uiux-preview/previewGate.js.
//
// The preview gate (shouldRenderUiUxPreview) blocks the redesign everywhere
// except a developer loopback: nodeEnv === 'development' + explicit enable
// flag + loopback hostname + the /app/ui-preview path. That gate stays fully
// authoritative for the preview route and is NOT imported, wrapped, or
// weakened here. The live mount is authorized by a different, explicit
// decision: allowLiveMount() opens the live surface only when the
// server-driven per-workspace map carried in GET /api/uiux-runtime/bootstrap
// (.bootstrap.workspaces / .workspaces) marks at least one workspace 'live'.
// Every failure — missing map, HTTP error, timeout, malformed payload,
// missing auth token — fails CLOSED to the existing UiUxRuntimeApp shell, so
// current production behavior is unchanged unless the server says otherwise.

import { useEffect, useState } from 'react';
import { createLiveApiClient } from './liveApiClient';
import { BOOTSTRAP_PATH, fetchBootstrapPayload } from './bootstrapCache';

export const LIVE_GATE_OPEN_VALUE = 'live';
export const LIVE_MOUNT_BOOTSTRAP_PATH = BOOTSTRAP_PATH;
const LIVE_MOUNT_PROBE_TIMEOUT_MS = 5_000;

const isPlainObject = (value) => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

// Mirrors liveCollections.extractWorkspaceGates's source resolution without
// importing the full collection registry into the main App.js bundle.
export const extractWorkspacesMapForMount = (payload) => {
  if (!isPlainObject(payload)) return null;
  if (isPlainObject(payload.bootstrap) && isPlainObject(payload.bootstrap.workspaces)) {
    return payload.bootstrap.workspaces;
  }
  if (isPlainObject(payload.workspaces)) return payload.workspaces;
  return null;
};

// The mount opens only on an explicit server-authored 'live' value. Any
// other value ('coming-soon', junk, non-string) keeps that workspace — and,
// when none is live, the whole mount — on the runtime shell.
export const allowLiveMount = (workspaceGates) => (
  isPlainObject(workspaceGates)
  && Object.values(workspaceGates).some((value) => value === LIVE_GATE_OPEN_VALUE)
);

// Never throws: every failure resolves to null (=> runtime surface).
// Goes through the shared bootstrap single-flight cache so the provider's
// workspaceGates collection reuses this probe's fetch instead of re-hitting
// the endpoint (post-login dead-time fix).
export const fetchLiveMountWorkspaces = async ({
  fetchImpl = null,
  storage = null,
  timeoutMs = LIVE_MOUNT_PROBE_TIMEOUT_MS,
} = {}) => {
  try {
    const client = createLiveApiClient({ fetchImpl, storage, timeoutMs });
    const payload = await fetchBootstrapPayload({ client });
    return extractWorkspacesMapForMount(payload);
  } catch (_error) {
    return null;
  }
};

// Surface selection for App.js: {status:'checking'|'ready',
// surface:'runtime'|'live', workspaceGates}. The surface is 'runtime' until
// the probe explicitly resolves live gates; pass enabled:false to skip the
// probe entirely (e.g. for users already redirected away from the pilot).
export const useLiveMountDecision = ({
  enabled = true,
  fetchImpl = null,
  storage = null,
  timeoutMs = LIVE_MOUNT_PROBE_TIMEOUT_MS,
} = {}) => {
  const [decision, setDecision] = useState({
    status: 'checking',
    surface: 'runtime',
    workspaceGates: null,
  });

  useEffect(() => {
    let active = true;
    if (!enabled) {
      setDecision({ status: 'ready', surface: 'runtime', workspaceGates: null });
      return () => { active = false; };
    }
    setDecision({ status: 'checking', surface: 'runtime', workspaceGates: null });
    fetchLiveMountWorkspaces({ fetchImpl, storage, timeoutMs }).then((workspaceGates) => {
      if (!active) return;
      setDecision({
        status: 'ready',
        surface: allowLiveMount(workspaceGates) ? 'live' : 'runtime',
        workspaceGates,
      });
    });
    return () => { active = false; };
  }, [enabled, fetchImpl, storage, timeoutMs]);

  return decision;
};
